import { useCallback, useMemo } from 'react';
import { Category } from 'shared/types/product';
import { Facet } from 'shared/types/result';
import { useTranslations } from 'use-intl';
import {
  FacetConfiguration,
  FacetFilter,
  TermFacet,
} from 'components/commercetools-ui/organisms/product/product-list/types';
import { P2R_VALUES } from 'helpers/constants/cart';
import { getConfigurationKey } from './helper';

interface Config {
  facets: Facet[];
  facetsConfiguration: FacetFilter[];
  categories: Category[];
}

const useFacetsConfiguration = ({ facets, facetsConfiguration, categories }: Config) => {
  const translate = useTranslations();

  const categoryNameById = useMemo(
    () => new Map(categories.map((category) => [category.categoryId, category.name])),
    [categories],
  );

  const isContainsP2RItems = useMemo(() => {
    const channels = facets.find((item) => item.key === 'variants.prices.channel');

    return (channels as TermFacet)?.terms.some((item) => P2R_VALUES.has(item.label));
  }, [facets]);

  const externalFacetsConfiguration = useMemo<Record<string, FacetFilter>>(
    () =>
      (facetsConfiguration ?? []).reduce(
        (acc, configuration) => ({
          ...acc,
          [configuration.key]: configuration,
        }),
        {} as Record<string, FacetFilter>,
      ),
    [facetsConfiguration],
  );

  const configurationLookup = useMemo(() => {
    const map = new Map<string, string>();

    Object.keys(externalFacetsConfiguration).forEach((key) => {
      map.set(key, key);

      const lastPart = key.split('.').pop();
      if (lastPart) {
        map.set(lastPart, key);
      }

      if (key.includes('.')) {
        const lastTwo = key.split('.').slice(-2).join('.');
        map.set(lastTwo, key);
      }
    });

    return map;
  }, [externalFacetsConfiguration]);

  const resolveConfigurationKey = useCallback(
    (facetKey: string) => getConfigurationKey(facetKey, configurationLookup),
    [configurationLookup],
  );

  const finalFacetsConfiguration = useMemo<Record<string, FacetConfiguration>>(() => {
    const finalFacets = [...(facets ?? [])];

    const keys = Object.keys(externalFacetsConfiguration);

    const orderMap = new Map(keys.map((key, index) => [key, index]));

    finalFacets.sort((a, b) => {
      const aConfigKey = resolveConfigurationKey(a.key);
      const bConfigKey = resolveConfigurationKey(b.key);

      const aIndex =
        aConfigKey != null ? (orderMap.get(aConfigKey) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;

      const bIndex =
        bConfigKey != null ? (orderMap.get(bConfigKey) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;

      return aIndex - bIndex;
    });

    return finalFacets
      .filter((facet) => !!resolveConfigurationKey(facet.key))
      .map((facet) => {
        const configKey = resolveConfigurationKey(facet.key);

        if (!configKey) {
          return facet;
        }

        if (facet.key === 'categories.id') {
          (facet as TermFacet).terms = (facet as TermFacet).terms.map((term) => ({
            ...term,
            label: categoryNameById.get(term.key) ?? '',
          }));
        } else if (facet.type === 'boolean') {
          (facet as TermFacet).terms = (facet as TermFacet).terms.map((term) => ({
            ...term,
            label: term.key === 'true' ? externalFacetsConfiguration[configKey].label : translate('product.regular'),
          }));
        }

        return facet;
      })
      .filter(
        (facet) =>
          facet.type !== 'boolean' || (facet as TermFacet).terms.some((term) => term.key === 'true' && term.count > 0),
      )
      .reduce(
        (acc, facet) => {
          const configKey = resolveConfigurationKey(facet.key);

          if (!configKey) {
            return acc;
          }

          acc[facet.key] = {
            ...facet,
            label: externalFacetsConfiguration[configKey].label,
            type: externalFacetsConfiguration[configKey].type,
          } as FacetConfiguration;

          return acc;
        },
        {} as Record<string, FacetConfiguration>,
      );
  }, [facets, externalFacetsConfiguration, resolveConfigurationKey, categoryNameById, translate]);

  return {
    finalFacetsConfiguration,
    isContainsP2RItems,
  };
};

export default useFacetsConfiguration;
